import React from "react";
import PropTypes from "prop-types";
import { kebabCase } from "lodash";

import { getImage } from "gatsby-plugin-image";
import { graphql, Link } from "gatsby";

import Seo from "../components/Seo";
import Layout from "../components/Layout";
import Content, { HTMLContent } from "../components/Content";
import { DiscussionEmbed } from "disqus-react";

import DisplayContentHeader from "../components/DisplayContentHeader";
import FullWidthImage from "../components/FullWidthImage";
import AuthorSummary from "../components/AuthorSummary";
import VideoPrevNext from "../components/VideoPrevNext";
import Summary from "../components/Summary";

import YouTube from "../components/YouTube";
import { nanoid } from "nanoid";

function returnAuthorSlugs(authorCollection) {
  let k = null;
  let v = null;
  let results = [];

  authorCollection.forEach((author) => {
    k = author.frontmatter.name;
    v = author.fields.slug;
    results[k] = v;
  });
  return results;
}

// eslint-disable-next-line
export const VideoPostTemplate = ({
  content,
  summary,
  category,
  tags,
  title,
  subtitle,
  image,
  smallImage,
  meta,
  helmet,
  slug,
  id,
  authors,
  config,
  trail,
  relatedPosts,
  youtubeId,
  gallery,
}) => {
  const PostContent = HTMLContent || Content;
  const heroImage = getImage(image) || image;

  let showTitle = meta.showTitle;
  let showSummary = meta.showSummary;
  let showTags = meta.showTags;
  let showComments = meta.showComments;
  let showAuthorSummary = meta.showAuthorSummary;
  let showPrevNext = meta.showPrevNext;

  let showRelated = meta.showRelated;
  let showLatest = meta.showLatest;

  config = config.frontmatter.blog;

  if (config.overrides.showTitle.enabled) {
    showTitle = config.overrides.showTitle.value;
  }
  if (config.overrides.showSummary.enabled) {
    showSummary = config.overrides.showSummary.value;
  }
  if (config.overrides.showTags.enabled) {
    showTags = config.overrides.showTags.value;
  }
  if (config.overrides.showComments.enabled) {
    showComments = config.overrides.showComments.value;
  }
  if (config.overrides.showAuthorSummary.enabled) {
    showAuthorSummary = config.overrides.showAuthorSummary.value;
  }
  if (config.overrides.showPrevNext.enabled) {
    showPrevNext = config.overrides.showPrevNext.value;
  }

  const author = meta.author;
  const creationDate = meta.creationDate;

  const disqusConfig = {
    shortname: process.env.GATSBY_DISQUS_NAME,
    config: { identifier: slug, title },
  };
  authors = authors.nodes;
  let authorSlugs = returnAuthorSlugs(authors);
  let authorSlug = authorSlugs[author];

  return (
    <div>
      {showTitle ? (
        <FullWidthImage
          img={heroImage}
          title={title}
          subheading={subtitle}
          imgPosition="center"
        />
      ) : (
        <FullWidthImage img={heroImage} imgPosition="center" />
      )}

      <section className="section section--gradient">
        {helmet || ""}

        <div className="container">
          <div className="content">
            <div className="content">
              <DisplayContentHeader
                title={title}
                parent="Videos"
                parentSlug="/videos"
                relatedPerson={author}
                relatedPersonType="Author"
                relatedPersonSlug={authorSlug}
                date={creationDate}
              />
            </div>
            {showSummary && summary ? (
              <div className="content">
                <Summary
                  summary={summary}
                  image={smallImage}
                  alt={title}
                  category={category}
                />
              </div>
            ) : null}
            <div className="content blog-post">
              {youtubeId && youtubeId !== '' && (
                <YouTube id={youtubeId} title={title} />
              )}

              <PostContent content={content} />
              <br />
              {showTags && tags && tags.length ? (
                <div style={{ marginTop: `2rem` }}>
                  <h4>Tags:</h4>
                  <ul className="taglist">
                    {tags.map((tag) => (
                      <li key={nanoid()}>
                        <Link to={`/tags/${kebabCase(tag)}/`}>{tag}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {gallery ? (
                <>
                  <div className="content">
                    <h4>Photo Gallery:</h4>
                    <p>
                      <Link
                        to={`${slug}gallery/`}
                        title={`${title} Photo Gallery`}
                      >
                        {title} Photos
                      </Link>
                    </p>
                  </div>
                </>
              ) : null}
              {showAuthorSummary ? <AuthorSummary author={author} /> : null}
            </div>
            {showComments ? (
              <div className="content">
                <DiscussionEmbed {...disqusConfig} />
              </div>
            ) : null}

            {showPrevNext ? (
              <div className="content">
                <h4>Keep Watching:</h4>
                <VideoPrevNext id={id} />
              </div>
            ) : null}

            {showRelated ? null : null}
            {showLatest ? null : null}
          </div>
        </div>
      </section>
    </div>
  );
};

VideoPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  helmet: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  summary: PropTypes.string,
  category: PropTypes.string,
  meta: PropTypes.shape({
    showTitle: PropTypes.bool,
    author: PropTypes.string,
    creationDate: PropTypes.string,
  }),
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  smallImage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  slug: PropTypes.string,
  id: PropTypes.string,
  authors: PropTypes.object,
  config: PropTypes.object,
  trail: PropTypes.object,
  relatedPosts: PropTypes.array,
  youtubeId: PropTypes.string,
};

const VideoPost = ({ data }) => {
  const { markdownRemark: post } = data;
  const { authors } = data;
  const { config } = data;

  let imageSrc =
    post.frontmatter.image.childImageSharp.gatsbyImageData.images.fallback.src;

  return (
    <Layout>
      <VideoPostTemplate
        content={post.html}
        helmet={
          <Seo
            title={`Video > ${post.frontmatter.category} > ${post.frontmatter.title}`}
            keywords={`mtb video, ${post.frontmatter.category.toLowerCase()} video, ${post.frontmatter.category.toLowerCase()}`}
            summary={post.frontmatter.summary}
            image={imageSrc}
            article={true}
            tags={post.frontmatter.tags}
          />
        }
        tags={post.frontmatter.tags}
        title={post.frontmatter.title}
        subtitle={post.frontmatter.subtitle}
        summary={post.frontmatter.summary}
        category={post.frontmatter.category}
        meta={post.frontmatter.meta}
        image={post.frontmatter.image}
        smallImage={post.frontmatter.smallImage}
        slug={post.fields.slug}
        id={post.id}
        authors={authors}
        config={config}
        trail={post.frontmatter.trail}
        relatedPosts={post.relatedPosts}
        youtubeId={post.frontmatter.youtubeId}
        gallery={post.frontmatter.gallery}
      />
    </Layout>
  );
};

VideoPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
    authors: PropTypes.object,
    site: PropTypes.object,
    config: PropTypes.object,
  }),
};

export default VideoPost;

export const pageQuery = graphql`
  query VideoPostByID($id: String!) {
    site {
      siteMetadata {
        title
        property {
          url
        }
      }
    }
    markdownRemark(id: { eq: $id }) {
      id
      html
      fields {
        slug
      }
      frontmatter {
        title
        subtitle
        summary
        category
        youtubeId
        tags
        trail {
          lon
          lat
        }
        image {
          childImageSharp {
            gatsbyImageData(
              quality: 100
              layout: FULL_WIDTH
              placeholder: BLURRED
            )
          }
        }
        smallImage
        meta {
          showTitle
          showSummary
          showTags
          showComments
          showAuthorSummary
          showPrevNext
          author
          creationDate(formatString: "MMMM Do, YYYY")
        }
        gallery {
          images {
            image {
              id
            }
          }
        }
      }
      relatedPosts {
        fields {
          slug
        }
        frontmatter {
          title
          meta {
            creationDate(formatString: "MMMM Do, YYYY")
            author
          }
        }
      }
    }
    authors: allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "author-post" } } }
    ) {
      nodes {
        id
        fields {
          slug
        }
        frontmatter {
          name
        }
      }
    }
    config: markdownRemark(
      frontmatter: { templateKey: { eq: "configuration" } }
    ) {
      frontmatter {
        blog {
          showImageAsOgImage
          overrides {
            showTitle {
              enabled
              value
            }
            showSummary {
              enabled
              value
            }
            showTags {
              enabled
              value
            }
            showComments {
              enabled
              value
            }
            showAuthorSummary {
              enabled
              value
            }
            showPrevNext {
              enabled
              value
            }
          }
        }
      }
    }
  }
`;
//cf3f4add-b6ab-51ed-a0f9-43a8901f01d0
